/**
 * Photo Viewer Core — Shared state and logic for mobile + desktop photo viewers.
 *
 * Platform composers (mobile/desktop) spread this into their Alpine data objects
 * and add platform-specific behavior (touch/swipe, keyboard nav, etc.).
 */

import { toggleLikeApi } from './alpine-like-button.js';

export function photoViewerCore(config) {
    const _albumId = String(config.albumId || '');
    const _userId = String(config.userId || '');
    const _prevPid = String(config.prevPid || '');
    const _nextPid = String(config.nextPid || '');
    const _base = `/app/photo?aid=${_albumId}&uid=${_userId}&pid=`;

    return {
        // Photo data
        photoUrl: config.photoUrl,
        photoId: String(config.photoId),
        prevUrl: config.prevUrl || (_prevPid ? _base + _prevPid : null),
        nextUrl: config.nextUrl || (_nextPid ? _base + _nextPid : null),
        albumUrl: config.albumUrl,
        username: config.username,
        userUrl: config.userUrl,
        description: config.description || '',
        timeAgo: config.timeAgo || '',

        // Like state
        isLiked: config.isLiked || false,
        likeCount: config.likeCount || 0,
        isLikeLoading: false,

        // Comment count
        commentCount: config.commentCount || 0,

        // Permissions
        isOwner: config.isOwner || false,
        isModerator: config.isModerator || false,

        // Album cover
        albumCoverId: String(config.albumCoverId || ''),
        get isCover() {
            return this.photoId && this.photoId === this.albumCoverId;
        },
        get editThumbnailUrl() {
            return '/app/albums/' + this._albumId + '/edit-contents';
        },
        settingCover: false,

        // Caption editing
        captionEditOpen: false,
        tempCaption: '',
        savingCaption: false,

        // Move to album
        otherAlbums: config.otherAlbums || [],
        moveModalOpen: false,
        movingPhoto: false,

        // Action URLs
        deleteUrl: config.deleteUrl || '',
        deleteConfirmText: config.deleteConfirmText || '',
        tineyeUrl: config.tineyeUrl || '',
        googleUrl: config.googleUrl || '',
        yandexUrl: config.yandexUrl || '',

        // UI state
        commentsOpen: false,
        menuOpen: false,

        // Navigation animation
        isNavigating: false,
        navigateDirection: 0,

        // SPA navigation state
        _prevPid: String(config.prevPid || ''),
        _nextPid: String(config.nextPid || ''),
        _albumId: String(config.albumId || ''),
        _userId: String(config.userId || ''),
        _commentsStale: false,
        _spaNavCount: 0,
        _hasInternalReferrer: (() => {
            try {
                return document.referrer && new URL(document.referrer).origin === window.location.origin;
            } catch { return false; }
        })(),

        // --- Preload ---

        _preloadAdjacentImages(cfg) {
            [cfg.prevImageUrl, cfg.nextImageUrl].forEach(url => {
                if (url) new Image().src = url;
            });
        },

        // --- Navigation ---

        goBack() {
            if (window._photoViewerGoingBack) return;
            window._photoViewerGoingBack = true;
            window.removeEventListener('popstate', this._popstateHandler);

            const stepsBack = this._spaNavCount + 1;

            setTimeout(() => {
                if (this._hasInternalReferrer) {
                    history.go(-stepsBack);
                } else {
                    window.location.href = this.albumUrl;
                }
                // Reset guard if navigation didn't happen
                setTimeout(() => { window._photoViewerGoingBack = false; }, 500);
            }, 0);
        },

        _hasPid(pid) {
            return !!pid;
        },

        navigateTo(direction) {
            const targetPid = direction > 0 ? this._nextPid : this._prevPid;
            const fallbackUrl = direction > 0 ? this.nextUrl : this.prevUrl;
            if (!this._hasPid(targetPid) && !fallbackUrl) return;

            this.isNavigating = true;
            this.navigateDirection = -direction;

            if (!this._hasPid(targetPid)) {
                setTimeout(() => { window.location.href = fallbackUrl; }, 30);
                return;
            }

            this._fetchPhoto(targetPid, direction, fallbackUrl);
        },

        async _fetchPhoto(pid, direction, fallbackUrl) {
            try {
                const resp = await fetch(`/app/helpers/photo?pid=${pid}&t=p`);
                if (!resp.ok) throw new Error('fetch failed');
                const data = await resp.json();

                await new Promise(r => setTimeout(r, 30));

                this._applyPhotoData(data);
                this._resetNavAnimation();

                const pageUrl = `/app/photo?pid=${data.pid}&aid=${this._albumId}&uid=${this._userId}`;
                history.pushState(null, '', pageUrl);
                this._spaNavCount++;

                this._preloadAdjacent(data);
            } catch {
                if (fallbackUrl) {
                    window.location.href = fallbackUrl;
                } else {
                    this._resetNavAnimation();
                }
            }
        },

        _applyPhotoData(data) {
            this.photoUrl = data.url;
            this.photoId = String(data.pid);
            this.description = data.d || '';
            this.timeAgo = data.a || '';
            this.isLiked = data.user_liked || false;
            this.likeCount = data.likes || 0;
            this.commentCount = data.comments || 0;

            this._prevPid = String(data.p || '');
            this._nextPid = String(data.n || '');

            const base = `/app/photo?aid=${this._albumId}&uid=${this._userId}&pid=`;
            this.prevUrl = this._hasPid(this._prevPid) ? base + this._prevPid : null;
            this.nextUrl = this._hasPid(this._nextPid) ? base + this._nextPid : null;

            this.deleteUrl = `/app/albums?todo=delete_photo&aid=${this._albumId}&pid=${data.pid}`;

            this.tineyeUrl = `http://www.tineye.com/search?url=${data.url}`;
            this.googleUrl = `https://lens.google.com/uploadbyurl?url=${data.url}`;
            this.yandexUrl = `https://www.yandex.com/images/search?rpt=imageview&url=${data.url}`;

            if (this.commentsOpen) {
                this._commentsStale = true;
                this._refetchCommentsOnNav();
            } else {
                this._commentsStale = true;
            }
            this._commentObserver?.disconnect();
            this._commentObserver = null;

            this._updateCommentForm(data.pid);
        },

        /**
         * Override in platform composers if comments need to auto-refetch
         * when navigating while comments panel is open (desktop sidebar).
         */
        _refetchCommentsOnNav() {
            this.commentsOpen = false;
        },

        _updateCommentForm(pid) {
            const container = this._getCommentsContainer();
            if (!container) return;
            const form = container.querySelector('.comment-form');
            if (!form) return;
            const entityInput = form.querySelector('input[name="entity_id"]');
            if (entityInput) entityInput.value = pid;
            form.id = `commentForm_${pid}`;
        },

        /**
         * Override in platform composers to return the comments DOM container.
         * Mobile uses $refs.commentsSheet, desktop uses $refs.commentsSidebar.
         */
        _getCommentsContainer() {
            return this.$refs.commentsSheet || this.$refs.commentsSidebar || null;
        },

        _resetNavAnimation() {
            this.isNavigating = false;
            this.navigateDirection = 0;
        },

        _preloadAdjacent(data) {
            [data.n_th?.url, data.p_th?.url, data.n_url, data.p_url].forEach(url => {
                if (url) new Image().src = url;
            });
        },

        // --- Like ---

        async toggleLike() {
            if (this.isLikeLoading) return;
            this.isLikeLoading = true;

            const wasLiked = this.isLiked;
            this.isLiked = !wasLiked;
            this.likeCount += wasLiked ? -1 : 1;

            try {
                await toggleLikeApi('photo', this.photoId, wasLiked);
            } catch {
                this.isLiked = wasLiked;
                this.likeCount += wasLiked ? 1 : -1;
                window.showError?.(window.t ? window.t('common.like_update_failed') : 'Failed to update like');
            } finally {
                this.isLikeLoading = false;
            }
        },

        // --- Comments ---

        openComments() {
            this.commentsOpen = true;
            if (this._commentsStale) {
                this._fetchComments();
            } else {
                this.$nextTick(() => {
                    const container = this._getCommentsContainer();
                    if (container) {
                        window.LucideIcons?.initialize(container);
                        this._observeCommentList();
                    }
                });
            }
        },

        async _fetchComments() {
            const container = this._getCommentsContainer();
            if (!container) return;

            const scrollContainer = container.querySelector('.overflow-y-auto');
            if (scrollContainer) scrollContainer.innerHTML = '';

            try {
                const params = new URLSearchParams({
                    entity_type: 'photo',
                    entity_id: this.photoId,
                    feed_owner_id: this._userId,
                    limit: '20'
                });
                const resp = await fetch(`/app/comment/fetch?${params}`);
                if (!resp.ok) throw new Error('fetch failed');
                const html = await resp.text();

                if (scrollContainer) {
                    scrollContainer.innerHTML = html;
                }

                this._commentsStale = false;

                this.$nextTick(() => {
                    window.LucideIcons?.initialize(container);
                    this._observeCommentList();
                });
            } catch {
                // Leave empty on error
            }
        },

        _observeCommentList() {
            if (this._commentObserver) return;
            const container = this._getCommentsContainer();
            if (!container) return;
            const commentList = container.querySelector('.comment-list');
            if (!commentList) return;

            this._commentObserver = new MutationObserver((mutations) => {
                for (const m of mutations) {
                    if (m.addedNodes.length) {
                        this.commentCount++;
                        window.LucideIcons?.initialize(container);
                        break;
                    }
                    if (m.removedNodes.length) {
                        this.commentCount = Math.max(0, this.commentCount - 1);
                        break;
                    }
                }
            });
            this._commentObserver.observe(commentList, { childList: true });
        },

        closeComments() {
            this.commentsOpen = false;
        },

        // --- Menu ---

        openMenu() {
            this.menuOpen = true;
        },

        closeMenu() {
            this.menuOpen = false;
        },

        async confirmDelete() {
            if (!confirm(this.deleteConfirmText)) {
                this.closeMenu();
                return;
            }
            this.closeMenu();

            const deleteUrl = `/app/albums?todo=delete_photo&aid=${this._albumId}&pid=${this.photoId}`;
            try {
                const resp = await fetch(deleteUrl);
                if (!resp.ok) throw new Error('Delete failed');
                window.location.href = this.albumUrl;
            } catch {
                window.location.href = deleteUrl;
            }
        },

        openModModal() {
            this.closeMenu();
            if (window.openPhotoModModal) {
                window.openPhotoModModal();
            } else {
                window.dispatchEvent(new CustomEvent('open-photo-mod-modal'));
            }
        },

        // --- Owner Actions: Set Cover ---

        async setAsCover() {
            if (this.settingCover || this.isCover) return;

            const token = await window.getApiToken?.();
            if (!token) return;

            this.settingCover = true;
            this.closeMenu();

            try {
                const formData = new FormData();
                formData.append('album_cover_id', this.photoId);

                const response = await fetch(`/v1/album/${this._albumId}`, {
                    method: 'PUT',
                    headers: { 'X-AUTH-TOKEN': token },
                    body: formData,
                    credentials: 'include'
                });

                if (!response.ok) throw new Error('Failed to set cover');

                this.albumCoverId = this.photoId;
            } catch {
                // Silently fail
            } finally {
                this.settingCover = false;
            }
        },

        // --- Owner Actions: Edit Caption ---

        openCaptionEdit() {
            this.closeMenu();
            this.tempCaption = this.description || '';
            this.captionEditOpen = true;
        },

        closeCaptionEdit() {
            this.captionEditOpen = false;
            this.tempCaption = '';
        },

        async saveCaption() {
            if (this.savingCaption) return;

            const token = await window.getApiToken?.();
            if (!token) return;

            this.savingCaption = true;

            try {
                const formData = new FormData();
                formData.append('description', this.tempCaption);

                const response = await fetch(`/v1/photo/${this.photoId}`, {
                    method: 'PUT',
                    headers: { 'X-AUTH-TOKEN': token },
                    body: formData,
                    credentials: 'include'
                });

                if (!response.ok) throw new Error('Failed to save caption');

                this.description = this.tempCaption;
                this.closeCaptionEdit();
            } catch {
                // Silently fail
            } finally {
                this.savingCaption = false;
            }
        },

        // --- Owner Actions: Move to Album ---

        openMoveModal() {
            this.closeMenu();
            this.moveModalOpen = true;
        },

        closeMoveModal() {
            this.moveModalOpen = false;
        },

        async moveToAlbum(targetAlbumId) {
            if (this.movingPhoto || !targetAlbumId) return;

            const token = await window.getApiToken?.();
            if (!token) return;

            this.movingPhoto = true;

            try {
                const formData = new FormData();
                formData.append('album_id', targetAlbumId);

                const response = await fetch(`/v1/photo/${this.photoId}`, {
                    method: 'PUT',
                    headers: { 'X-AUTH-TOKEN': token },
                    body: formData,
                    credentials: 'include'
                });

                if (!response.ok) throw new Error('Failed to move photo');

                this.closeMoveModal();
                this._albumId = String(targetAlbumId);
                this.albumUrl = '/app/album?aid=' + targetAlbumId + '&uid=' + this._userId;

                // Navigate to the new album
                window.location.href = this.albumUrl;
            } catch {
                // Silently fail
            } finally {
                this.movingPhoto = false;
            }
        }
    };
}
